// Cockpit instruments: top-strip gauges (live / objects / alerts + event-rate
// sparkline), the live ticker, geocoder search, entity rows, toasts.

import * as maplibregl from 'maplibre-gl';
import type { EntityDto, RegistryDto } from './api';
import { get } from './api';
import { fmtCoords, parseCoords } from './coords';
import { relTimeMs } from './when';

/** True when the UI is in its phone layout — narrow, OR short enough that a
 *  landscape phone would lose its side panels off the bottom of the screen.
 *  This MUST stay in step with the media query mobile.css reshapes on: the
 *  drawer that becomes a bottom sheet there needs matching behaviour here
 *  (the header is a swipe handle, a tool pick stands the sheet down, the
 *  Studio lands on the imagery not on a control sheet). One definition so
 *  the two can't drift apart. */
export const COMPACT_QUERY = '(max-width: 820px), (max-height: 500px)';
export const isCompactViewport = (): boolean => matchMedia(COMPACT_QUERY).matches;

export function entityRow(e: EntityDto, registry: RegistryDto | null,
                          onClick: () => void): HTMLElement {
  const type = registry?.types.find((t) => t.key === e.type);
  const row = document.createElement('div');
  row.className = 'row entity-row';
  // Unnamed objects lead with their date when they have one (damage markers).
  const date = typeof e.attrs.date === 'string' ? e.attrs.date : '';
  const note = typeof e.attrs.note === 'string' ? e.attrs.note : '';
  const title = e.name || date || '(unnamed)';
  const ranges: string[] = [];
  if (typeof e.attrs.range_km === 'number') ranges.push(`${e.attrs.range_km} km`);
  if (typeof e.attrs.radar_range_km === 'number') {
    ranges.push(`radar ${e.attrs.radar_range_km} km`);
  }
  const status = typeof e.attrs.status === 'string' ? e.attrs.status : '';
  const subtitle = [type?.label ?? e.type,
                    status,
                    ranges.join(' · '),
                    (e.attrs.country as string) || (e.attrs.place as string) || '',
                    note.length > 46 ? `${note.slice(0, 45)}…` : note]
    .filter(Boolean).join(' · ');
  const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  row.innerHTML = `<span class="dot" style="background:${type?.style.color ?? '#2f6fed'}"></span>
    <span class="t"><span class="n">${esc(title || '(unnamed)')}</span>
    <span class="s">${esc(subtitle)}</span></span>`;
  row.addEventListener('click', onClick);
  return row;
}

/** Relative age of an ISO instant. Delegates to when.relTimeMs so there is ONE
 *  implementation: this version rendered a future timestamp as "1s" —
 *  indistinguishable from the freshest possible event — and an unparseable one
 *  as "NaNs". Callers here are row/creation times (createdAt, updatedAt,
 *  validFrom), which is the right clock for an activity log. */
export function relTime(iso: string): string {
  return relTimeMs(Date.parse(iso));
}

// --- top-strip gauges --------------------------------------------------------

export interface LiveStateDto {
  on: boolean;
  polls: number;
  lastPollAt: string | null;
  lastError: string | null;
  eventCount: number;
  channels: string[];
}

function sparkSvg(buckets: number[]): string {
  const w = 56;
  const h = 16;
  const max = Math.max(1, ...buckets);
  const step = w / Math.max(1, buckets.length - 1);
  const pts = buckets.map((v, i) =>
    `${(i * step).toFixed(1)},${(h - 1.5 - (v / max) * (h - 3)).toFixed(1)}`).join(' ');
  return `<svg viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" class="spark">
    <polyline points="${pts}" fill="none" stroke="currentColor" stroke-width="1.6"
      stroke-linecap="round" stroke-linejoin="round" /></svg>`;
}

export function installGauges(el: HTMLElement, isAdmin: boolean, onAdmin: () => void): {
  update: (live: LiveStateDto | null, alerts: number, objects: number) => void;
  setSpark: (buckets: number[]) => void;
} {
  el.innerHTML = `
    <span class="gauge"><span class="v" id="g-live"><span class="pip"></span>—</span><span class="k">live</span></span>
    <span class="gauge gauge-spark" id="g-spark" title="Live events · last 6 h"></span>
    <span class="gauge"><span class="v" id="g-objects">—</span><span class="k">objects</span></span>
    <span class="gauge"><span class="v" id="g-alerts">0</span><span class="k">alerts</span></span>
    ${isAdmin ? '<button class="iconbtn" id="g-admin" title="Admin">⚙</button>' : ''}`;
  document.getElementById('g-admin')?.addEventListener('click', onAdmin);
  let lastAlerts = 0;
  return {
    update(live, alerts, objects) {
      const g = document.getElementById('g-live');
      if (g) {
        const pip = `<span class="pip${live?.on ? ' on' : ''}"></span>`;
        const label = live == null ? 'n/a'
          : live.on ? (live.lastPollAt ? relTime(live.lastPollAt) : 'on') : 'off';
        g.innerHTML = pip + label;
        g.title = live?.lastError ? `Last error: ${live.lastError}` : `${live?.polls ?? 0} polls`;
      }
      const ob = document.getElementById('g-objects');
      if (ob) ob.textContent = String(objects);
      const al = document.getElementById('g-alerts');
      if (al) {
        al.textContent = String(alerts);
        al.style.color = alerts > 0 ? 'var(--warn)' : '';
        if (alerts > lastAlerts) {
          al.classList.remove('flash');
          void al.offsetWidth;           // restart the animation
          al.classList.add('flash');
        }
        lastAlerts = alerts;
      }
    },
    setSpark(buckets) {
      const host = document.getElementById('g-spark');
      if (host) host.innerHTML = sparkSvg(buckets);
    },
  };
}

// --- live ticker -------------------------------------------------------------

export interface TickerItem {
  glyph: string;
  color: string;      // age pin colour
  place: string;
  text: string;
  at: number;         // epoch ms — WHEN IT HAPPENED, not when the row was written
  id?: string;        // entity id, for click-through
  coords?: [number, number];
}

export function installTicker(el: HTMLElement, onClick: () => void): {
  set: (item: TickerItem | null) => void;
} {
  let lastKey = '';
  el.addEventListener('click', onClick);
  return {
    set(item) {
      if (!item) {
        el.innerHTML = '';
        el.classList.remove('has-item');
        lastKey = '';
        return;
      }
      const key = `${item.at}|${item.place}`;
      const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      el.innerHTML = `<span class="tk-pip" style="background:${item.color}"></span>
        <time>${relTimeMs(item.at)}</time>
        <span class="tk-g">${item.glyph}</span>
        <span class="tk-t"><b>${esc(item.place)}</b>${item.text ? ` — ${esc(item.text)}` : ''}</span>`;
      el.classList.add('has-item');
      if (key !== lastKey) {
        el.classList.remove('swap');
        void el.offsetWidth;
        el.classList.add('swap');
        lastKey = key;
      }
    },
  };
}

// --- search -----------------------------------------------------------------

interface GeocodeHit { label: string; lat: number; lon: number; bbox: number[] | null; }

export function installSearch(input: HTMLInputElement, resultsHost: HTMLElement,
                              map: maplibregl.Map): void {
  let timer: number | undefined;

  // A pasted coordinate needs a mark to survive the first pan — at z14 "the
  // point is the viewport centre" stops being true the moment the camera
  // moves. One ephemeral pin, replaced by the next coordinate search,
  // dismissed by clicking it.
  let pin: maplibregl.Marker | null = null;
  const dropPin = (lon: number, lat: number): void => {
    pin?.remove();
    pin = new maplibregl.Marker().setLngLat([lon, lat]).addTo(map);
    const el = pin.getElement();
    el.style.cursor = 'pointer';
    el.title = 'Click to dismiss';
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      pin?.remove();
      pin = null;
    });
  };

  input.addEventListener('input', () => {
    window.clearTimeout(timer);
    const q = input.value.trim();
    if (q.length < 2) { resultsHost.innerHTML = ''; return; }

    // Coordinates resolve locally and NEVER reach the geocoder — Nominatim
    // doesn't answer them, and its 1 req/s budget is shared with live-event
    // resolution. Rendered synchronously: no debounce for a local parse.
    const pt = parseCoords(q);
    if (pt) {
      resultsHost.innerHTML = '';
      const div = document.createElement('div');
      // The swap is said in words, per the basis rule — a silently reordered
      // pair is how someone ends up staring at the wrong hemisphere.
      div.textContent = `→ ${fmtCoords(pt)}`
        + (pt.swapped ? ' — input read as lon, lat' : '');
      div.addEventListener('click', () => {
        resultsHost.innerHTML = '';
        input.value = '';
        map.flyTo({ center: [pt.lon, pt.lat], zoom: Math.max(map.getZoom(), 14) });
        dropPin(pt.lon, pt.lat);
      });
      resultsHost.appendChild(div);
      return;
    }

    timer = window.setTimeout(async () => {
      try {
        const { results } = await get<{ results: GeocodeHit[] }>(
          `/api/geocode?q=${encodeURIComponent(q)}`);
        resultsHost.innerHTML = '';
        for (const hit of results.slice(0, 6)) {
          const div = document.createElement('div');
          div.textContent = hit.label;
          div.addEventListener('click', () => {
            resultsHost.innerHTML = '';
            input.value = '';
            if (hit.bbox) {
              // Nominatim bbox order: [south, north, west, east]
              map.fitBounds([[hit.bbox[2]!, hit.bbox[0]!], [hit.bbox[3]!, hit.bbox[1]!]],
                            { padding: 60, maxZoom: 13 });
            } else {
              map.flyTo({ center: [hit.lon, hit.lat], zoom: 11 });
            }
          });
          resultsHost.appendChild(div);
        }
      } catch { /* transient */ }
    }, 350);
  });
  // Enter commits the top result — the paste-coordinates-hit-Enter flow.
  // For geocode results this activates the best hit once the list is up.
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      (resultsHost.firstElementChild as HTMLElement | null)?.click();
    }
  });
  document.addEventListener('click', (e) => {
    if (!resultsHost.contains(e.target as Node) && e.target !== input) {
      resultsHost.innerHTML = '';
    }
  });
}

// --- tools ------------------------------------------------------------------

export type ToolId = 'select' | 'add' | 'area' | 'scan' | 'measure'
  | 'markup' | 'time';

export function toast(text: string): void {
  const wrap = document.getElementById('toasts');
  if (!wrap) return;
  const el = document.createElement('div');
  el.className = 'toast';
  el.textContent = text;
  wrap.appendChild(el);
  setTimeout(() => el.remove(), 8000);
}
